import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Activity, Wrench, Calendar, AlertTriangle, TrendingUp, Droplets, Clock, Power } from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [upcomingMaintenance, setUpcomingMaintenance] = useState([]);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [utaStatus, setUtaStatus] = useState([]);
  const [currentSeason, setCurrentSeason] = useState('inverno');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    // Update UTA status every minute
    const interval = setInterval(() => {
      fetchUtaStatus();
    }, 60000);
    return () => clearInterval(interval);
  }, [currentSeason]);

  const fetchUtaStatus = async () => {
    try {
      const response = await axiosInstance.get(`/uta-schedules/status/${currentSeason}`);
      setUtaStatus(response.data);
    } catch (error) {
      console.error('Error fetching UTA status:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [statsRes, upcomingRes, alertsRes] = await Promise.all([
        axiosInstance.get('/maintenance/stats'),
        axiosInstance.get('/scheduled/upcoming'),
        axiosInstance.get('/alerts'),
        fetchUtaStatus()
      ]);

      setStats(statsRes.data);
      setUpcomingMaintenance(upcomingRes.data);
      setRecentAlerts(alertsRes.data.filter(a => !a.resolved).slice(0, 5));
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Manutenzioni Totali',
      value: stats?.totalRecords || 0,
      icon: Wrench,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Sostituzioni Gel',
      value: stats?.replacements?.gel || 0,
      icon: Droplets,
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Sostituzioni Calotta',
      value: stats?.replacements?.cap || 0,
      icon: AlertTriangle,
      color: 'from-orange-500 to-amber-500'
    },
    {
      title: 'Sostituzioni Sonda pH',
      value: stats?.replacements?.phProbe || 0,
      icon: Activity,
      color: 'from-purple-500 to-pink-500'
    }
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Benvenuto, {user.name}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="card-hover border-0 shadow-lg overflow-hidden" data-testid={`stat-card-${index}`}>
              <div className={`h-2 bg-gradient-to-r ${stat.color}`}></div>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-gray-600">{stat.title}</CardTitle>
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.color} bg-opacity-10`}>
                    <Icon className="w-5 h-5 text-gray-700" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-gray-900" data-testid={`stat-value-${index}`}>{stat.value}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* UTA Status */}
      {utaStatus.length > 0 && (
        <Card className="shadow-lg border-0">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Clock className="w-5 h-5 mr-2 text-blue-600" />
                <CardTitle>Stato UTA - {currentSeason === 'inverno' ? 'Inverno' : 'Estate'}</CardTitle>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentSeason('inverno')}
                  className={`px-3 py-1 rounded text-sm ${currentSeason === 'inverno' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  Inverno
                </button>
                <button
                  onClick={() => setCurrentSeason('estate')}
                  className={`px-3 py-1 rounded text-sm ${currentSeason === 'estate' ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  Estate
                </button>
              </div>
            </div>
            <CardDescription>
              Orario corrente: {utaStatus[0]?.current_time} - {utaStatus[0]?.day_of_week && utaStatus[0].day_of_week.charAt(0).toUpperCase() + utaStatus[0].day_of_week.slice(1)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {utaStatus.map((uta, index) => (
                <div key={index} className={`p-4 rounded-lg border-2 ${uta.is_on ? 'bg-green-50 border-green-500' : 'bg-gray-50 border-gray-300'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900 text-sm">{uta.uta_name}</h3>
                    <div className="flex items-center">
                      <Power className={`w-5 h-5 ${uta.is_on ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className={`ml-2 font-bold ${uta.is_on ? 'text-green-600' : 'text-gray-400'}`}>
                        {uta.is_on ? 'ACCESA' : 'SPENTA'}
                      </span>
                    </div>
                  </div>
                  {uta.has_schedule ? (
                    <div className="space-y-1">
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="font-medium text-green-600">{uta.turn_on_time}</span>
                        <span className="mx-2">→</span>
                        <span className="font-medium text-red-600">{uta.turn_off_time}</span>
                      </div>
                      {uta.turn_on_time_2 && uta.turn_off_time_2 && (
                        <div className="flex items-center text-xs text-gray-600">
                          <span className="font-medium text-green-600">{uta.turn_on_time_2}</span>
                          <span className="mx-2">→</span>
                          <span className="font-medium text-red-600">{uta.turn_off_time_2}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 italic">Nessun orario configurato per oggi</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Maintenance */}
        <Card className="shadow-lg border-0">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-blue-600" />
              Prossime Manutenzioni
            </CardTitle>
            <CardDescription>Manutenzioni programmate in arrivo</CardDescription>
          </CardHeader>
          <CardContent>
            {upcomingMaintenance.length === 0 ? (
              <p className="text-gray-500 text-center py-8" data-testid="no-upcoming-maintenance">Nessuna manutenzione programmata</p>
            ) : (
              <div className="space-y-3" data-testid="upcoming-maintenance-list">
                {upcomingMaintenance.map((item) => (
                  <div key={item.id} className="flex items-start p-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors" data-testid={`upcoming-item-${item.id}`}>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.equipmentName}</p>
                      <p className="text-sm text-gray-600">{item.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Scadenza: {new Date(item.nextDue).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                    <span className="badge badge-blue">{item.frequency}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Alerts */}
        <Card className="shadow-lg border-0">
          <CardHeader>
            <CardTitle className="flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-orange-600" />
              Alert Attivi
            </CardTitle>
            <CardDescription>Notifiche che richiedono attenzione</CardDescription>
          </CardHeader>
          <CardContent>
            {recentAlerts.length === 0 ? (
              <p className="text-gray-500 text-center py-8" data-testid="no-alerts">Nessun alert attivo</p>
            ) : (
              <div className="space-y-3" data-testid="alerts-list">
                {recentAlerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={`flex items-start p-3 rounded-lg ${
                      alert.priority === 'high'
                        ? 'bg-red-50 border-l-4 border-red-500'
                        : alert.priority === 'medium'
                        ? 'bg-orange-50 border-l-4 border-orange-500'
                        : 'bg-yellow-50 border-l-4 border-yellow-500'
                    }`}
                    data-testid={`alert-${alert.id}`}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{alert.message}</p>
                      {alert.equipmentName && (
                        <p className="text-sm text-gray-600 mt-1">{alert.equipmentName}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(alert.createdAt).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                    <span
                      className={`badge ${
                        alert.priority === 'high'
                          ? 'badge-red'
                          : alert.priority === 'medium'
                          ? 'badge-yellow'
                          : 'badge-blue'
                      }`}
                    >
                      {alert.priority}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;